import { useEffect, useState } from "react";
import { Routes, Route, NavLink, Link, Navigate, useLocation } from "react-router-dom";
import Logo from "./components/Logo.jsx";
import ConfirmDialog from "./components/ConfirmDialog.jsx";
import Collection from "./pages/Collection.jsx";
import AllCards from "./pages/AllCards.jsx";
import SetDetail from "./pages/SetDetail.jsx";
import CardInfo from "./pages/CardInfo.jsx";
import Landing from "./pages/Landing.jsx";
import Sales from "./pages/Sales.jsx";
import Stats from "./pages/Stats.jsx";
import Import from "./pages/Import.jsx";
import Impressum from "./pages/Impressum.jsx";
import Datenschutz from "./pages/Datenschutz.jsx";
import Nutzungsbedingungen from "./pages/Nutzungsbedingungen.jsx";
import Unsubscribe from "./pages/Unsubscribe.jsx";
import Login from "./pages/Login.jsx";
import Register from "./pages/Register.jsx";
import ForgotPassword from "./pages/ForgotPassword.jsx";
import ResetPassword from "./pages/ResetPassword.jsx";
import VerifyEmail from "./pages/VerifyEmail.jsx";
import Account from "./pages/Account.jsx";
import Orden from "./pages/Orden.jsx";
import Watchlist from "./pages/Watchlist.jsx";
import NotFound from "./pages/NotFound.jsx";
import News from "./pages/News.jsx";
import Article from "./pages/Article.jsx";
import ArtistCards from "./pages/ArtistCards.jsx";
import Footer from "./components/Footer.jsx";
import FeedbackButton from "./components/FeedbackButton.jsx";
import AccountMenu from "./components/AccountMenu.jsx";
import NavMenu from "./components/NavMenu.jsx";
import ErrorBoundary from "./components/ErrorBoundary.jsx";
import { useTheme } from "./hooks/useTheme.js";
import { SITE_TITLE } from "./hooks/usePageTitle.js";
import { useAuth } from "./context/AuthContext.jsx";

const navCls = ({ isActive }) =>
  isActive ? "text-ink font-medium" : "text-subtle hover:text-ink";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
}

// Tab-Titel der festen Seiten; Karten-, Set- und Illustratorseiten setzen ihren
// eigenen, sobald die Daten da sind.
const PAGE_TITLES = {
  "/sammlung": "Meine Sammlung",
  "/sets": "Alle Karten & Sets",
  "/news": "News & Artikel",
  "/impressum": "Impressum",
  "/datenschutz": "Datenschutzerklärung",
  "/nutzungsbedingungen": "Nutzungsbedingungen",
  "/login": "Anmelden",
  "/register": "Registrieren",
  "/import": "Import",
  "/verkauft": "Verkäufe",
  "/analyse": "Analyse",
  "/konto": "Konto",
  "/orden": "Orden",
  "/watchlist": "Watchlist",
};

export default function App() {
  const { isDark, toggleTheme } = useTheme();
  const { user, loading, logout, registrationOpen } = useAuth();
  const [confirmLogout, setConfirmLogout] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const t = PAGE_TITLES[location.pathname];
    document.title = t ? `${t} | mycardfolio` : SITE_TITLE;
  }, [location.pathname]);

  // "Menschlicher Besucher": meldet einmal pro Tag und Browser-Sitzung, dass auf der Seite etwas
  // getan wurde (scrollen, klicken, tippen, wischen). Roboter tun das nicht. Kein Cookie, kein Tracker.
  useEffect(() => {
    if (navigator.webdriver) return undefined; // automatisierte Browser
    const key = `mcf-ping-${new Date().toDateString()}`;
    try {
      if (sessionStorage.getItem(key)) return undefined;
    } catch {
      /* ohne Speicher: einmal pro Seitenaufruf */
    }
    const events = ["pointerdown", "keydown", "scroll", "touchstart", "wheel"];
    const ping = () => {
      events.forEach((e) => window.removeEventListener(e, ping));
      try {
        sessionStorage.setItem(key, "1");
      } catch {
        /* egal */
      }
      fetch("/api/ping", { method: "POST", keepalive: true }).catch(() => {});
    };
    events.forEach((e) => window.addEventListener(e, ping, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, ping));
  }, []);

  async function doLogout() {
    setLoggingOut(true);
    try {
      await logout();
    } finally {
      setLoggingOut(false);
      setConfirmLogout(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-line px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <NavMenu user={user} loading={loading} />
          <Link to="/" aria-label="mycardfolio – Startseite" className="shrink-0">
            <Logo className="h-6 sm:h-11" />
          </Link>
        </div>
        <div className="flex items-center gap-4 sm:gap-6">
          <nav className="hidden sm:flex gap-6 text-sm">
            <NavLink to="/" end className={navCls}>Start</NavLink>
            <NavLink to="/sets" className={navCls}>Alle Karten</NavLink>
            <NavLink to="/news" className={navCls}>News</NavLink>
            {user && (
              <>
                <NavLink to="/sammlung" id="nav-sammlung" className={navCls}>Sammlung</NavLink>
                <NavLink to="/analyse" className={navCls}>Analyse</NavLink>
                <NavLink to="/orden" id="nav-orden" className={navCls}>Orden</NavLink>
              </>
            )}
          </nav>
          {!loading && !user && (
            <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm">
              <NavLink to="/login" className={(s) => `hidden sm:inline ${navCls(s)}`}>
                Anmelden
              </NavLink>
              {registrationOpen && (
                <Link to="/register" className="bg-yellow text-yellowInk font-medium px-3 py-1.5 rounded-full text-xs sm:text-sm whitespace-nowrap">
                  Registrieren
                </Link>
              )}
            </div>
          )}
          <div className="flex items-center gap-2">
            {!loading && user && (
              <AccountMenu email={user.email} onLogout={() => setConfirmLogout(true)} />
            )}
            {/* Angemeldet: Hell-/Dunkelmodus lebt unter "Konto". Ohne Konto
                gibt es diese Seite nicht, deshalb hier der einzige Schalter. */}
            {!loading && !user && (
              <button
                onClick={toggleTheme}
                aria-label="Hell-/Dunkelmodus wechseln"
                className="w-8 h-8 flex items-center justify-center rounded-full border border-line text-sm"
              >
                {isDark ? "☀︎" : "☾"}
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="px-6 py-8 max-w-5xl mx-auto w-full flex-1">
        {/* key=Pfad: fängt ein ein Render-Fehler einer Seite ab, statt dass
            die ganze App hängen bleibt und nur ein manuelles Neuladen hilft -
            bei jedem Seitenwechsel wird frisch gemountet, also eine neue
            Chance statt dauerhaft im Fehlerzustand stecken zu bleiben. */}
        <ErrorBoundary key={location.pathname}>
          <Routes>
            {/* Öffentlich */}
            <Route path="/login" element={user ? <Navigate to="/" replace /> : <Login />} />
            <Route path="/register" element={user ? <Navigate to="/" replace /> : <Register />} />
            <Route path="/passwort-vergessen" element={<ForgotPassword />} />
            <Route path="/passwort-zuruecksetzen" element={<ResetPassword />} />
            <Route path="/verify" element={<VerifyEmail />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/nutzungsbedingungen" element={<Nutzungsbedingungen />} />
            <Route path="/abmelden" element={<Unsubscribe />} />

            {/* Kartensuche & -datenbank: auch ohne Konto nutzbar */}
            <Route path="/sets" element={<AllCards />} />
            <Route path="/news" element={<News />} />
            <Route path="/news/:slug" element={<Article />} />
            <Route path="/sets/:setId" element={<SetDetail />} />
            <Route path="/database/:externalId" element={<CardInfo />} />
            <Route path="/illustrator/:name" element={<ArtistCards />} />
            {/* "Hinzufügen" ist jetzt Teil von "Alle Karten" */}
            <Route path="/add" element={<Navigate to="/sets" replace />} />

            {/* Start: Landingpage; angemeldet mit Portfolio-Fenster statt Werbeteilen */}
            <Route path="/" element={loading ? null : <Landing />} />
            <Route path="/sammlung" element={<RequireAuth><Collection /></RequireAuth>} />

            {/* Nur mit Login */}
            <Route path="/import" element={<RequireAuth><Import /></RequireAuth>} />
            <Route path="/verkauft" element={<RequireAuth><Sales /></RequireAuth>} />
            <Route path="/analyse" element={<RequireAuth><Stats /></RequireAuth>} />
            <Route path="/statistik" element={<Navigate to="/analyse" replace />} />
            <Route path="/konto" element={<RequireAuth><Account /></RequireAuth>} />
            <Route path="/orden" element={<RequireAuth><Orden /></RequireAuth>} />
            <Route path="/watchlist" element={<RequireAuth><Watchlist /></RequireAuth>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </ErrorBoundary>
      </main>

      <Footer />
      <FeedbackButton />

      {confirmLogout && (
        <ConfirmDialog
          title="Wirklich abmelden?"
          message="Du kannst dich jederzeit wieder anmelden."
          confirmLabel="Abmelden"
          busy={loggingOut}
          onConfirm={doLogout}
          onClose={() => !loggingOut && setConfirmLogout(false)}
        />
      )}
    </div>
  );
}
